import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Camera, ChevronLeft, ChevronRight, MapPin, FileText, Calendar as CalIcon } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MONTHS_TR = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
const DAYS_TR = ["Pzt","Sal","Çar","Per","Cum","Cmt","Paz"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfWeek = (d) => { const day = d.getDay(); const diff = (day + 6) % 7; const nd = new Date(d); nd.setDate(d.getDate() - diff); nd.setHours(0, 0, 0, 0); return nd; };
const addDays = (d, n) => { const nd = new Date(d); nd.setDate(d.getDate() + n); return nd; };

const STATUS = {
  onay_bekleniyor: { chip: "bg-amber-100 text-amber-800 border-amber-300", dot: "bg-amber-500", label: "Onay Bekleniyor" },
  onaylandi: { chip: "bg-emerald-100 text-emerald-800 border-emerald-300", dot: "bg-emerald-500", label: "Onaylandı" },
  iptal: { chip: "bg-red-100 text-red-800 border-red-300", dot: "bg-red-500", label: "İptal", strike: true },
};

export default function PublicPhotographerSchedule() {
  const { token } = useParams();
  const [refDate, setRefDate] = useState(() => new Date());
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const range = useMemo(() => {
    const first = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
    const last = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0);
    return { from: iso(first), to: iso(last) };
  }, [refDate]);

  useEffect(() => { (async () => {
    try {
      const { data } = await axios.get(`${API}/api/public/photographer/${token}`, { params: { date_from: range.from, date_to: range.to } });
      setData(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Link geçersiz veya iptal edilmiş");
    }
  })(); }, [token, range.from, range.to]);

  const shootsByDate = useMemo(() => {
    const m = {};
    (data?.shoots || []).forEach((s) => { (m[s.date] ||= []).push(s); });
    return m;
  }, [data]);

  const monthGrid = useMemo(() => {
    const first = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
    const start = startOfWeek(first);
    return Array.from({ length: 42 }, (_, i) => addDays(start, i));
  }, [refDate]);

  const nav = (dir) => { const nd = new Date(refDate); nd.setMonth(refDate.getMonth() + dir); setRefDate(nd); };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="max-w-md text-center bg-white rounded-2xl border border-slate-100 p-10">
          <div className="w-12 h-12 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-4"><CalIcon size={22} /></div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Link Geçersiz</h1>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500 text-sm">Yükleniyor…</div>;
  }

  const fullName = `${data.photographer.first_name} ${data.photographer.last_name}`.trim();
  const upcoming = (data.shoots || []).filter((s) => s.status !== "iptal");
  const total = data.shoots?.length || 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-6 py-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center"><Camera size={22} /></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="pub-title">{fullName} — Aylık Çekim Planı</h1>
            <p className="text-xs text-slate-500 mt-1">Sadece görüntüleme · Ajans CRM tarafından paylaşıldı</p>
          </div>
          <div className="ml-auto text-right">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Bu Ay</div>
            <div className="text-lg font-bold text-slate-900" data-testid="pub-count">{total} çekim</div>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <button onClick={() => nav(-1)} className="w-9 h-9 rounded-full border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center justify-center" data-testid="pub-prev"><ChevronLeft size={14} /></button>
            <div className="text-lg font-semibold text-slate-900 min-w-[200px] text-center" data-testid="pub-month">{MONTHS_TR[refDate.getMonth()]} {refDate.getFullYear()}</div>
            <button onClick={() => nav(1)} className="w-9 h-9 rounded-full border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center justify-center" data-testid="pub-next"><ChevronRight size={14} /></button>
            <button onClick={() => setRefDate(new Date())} className="ml-2 rounded-full border border-slate-200 bg-white hover:bg-slate-50 px-3 py-1.5 text-xs" data-testid="pub-today">Bugün</button>
          </div>
          <div className="flex items-center gap-3 text-xs">
            {Object.entries(STATUS).map(([k, s]) => (
              <span key={k} className="inline-flex items-center gap-1.5 text-slate-600"><span className={`w-2 h-2 rounded-full ${s.dot}`} />{s.label}</span>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden mb-8" data-testid="pub-calendar">
          <div className="grid grid-cols-7 border-b border-slate-100">
            {DAYS_TR.map((d) => <div key={d} className="py-3 text-center text-[10px] font-semibold uppercase text-slate-400 tracking-wider">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {monthGrid.map((d, i) => {
              const inMonth = d.getMonth() === refDate.getMonth();
              const today = iso(d) === iso(new Date());
              const dsh = shootsByDate[iso(d)] || [];
              return (
                <div key={i} className={`min-h-[100px] border-b border-l border-slate-50 p-2 ${inMonth ? "bg-white" : "bg-slate-50/40"}`}>
                  <div className={`text-xs font-semibold mb-1 ${today ? "text-violet-700" : inMonth ? "text-slate-700" : "text-slate-400"}`}>{d.getDate()}</div>
                  <div className="space-y-1">
                    {dsh.map((s) => {
                      const st = STATUS[s.status] || STATUS.onay_bekleniyor;
                      return (
                        <div key={s.id} className={`rounded px-1.5 py-0.5 border ${st.chip} text-[10px] font-medium truncate ${st.strike ? "line-through" : ""}`} title={`${s.start_time} · ${s.brand_name}`}>
                          {s.start_time} {s.brand_name}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-6" data-testid="pub-list">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">Bu Aya Ait Çekimler</h2>
          {upcoming.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">Bu ay planlanan çekim yok.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {upcoming.map((s) => {
                const st = STATUS[s.status] || STATUS.onay_bekleniyor;
                return (
                  <div key={s.id} className="py-4 flex gap-4 first:pt-0 last:pb-0" data-testid={`pub-shoot-${s.id}`}>
                    <div className="w-20 text-right">
                      <div className={`text-xs text-slate-400 ${st.strike ? "line-through" : ""}`}>{s.date}</div>
                      <div className={`text-sm font-semibold text-slate-900 ${st.strike ? "line-through" : ""}`}>{s.start_time}</div>
                      <div className={`text-[11px] text-slate-500 ${st.strike ? "line-through" : ""}`}>– {s.end_time}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`text-base font-semibold text-slate-900 ${st.strike ? "line-through" : ""}`}>{s.brand_name}</div>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border ${st.chip}`}><span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{st.label}</span>
                      </div>
                      {s.location && <div className="text-xs text-slate-600 flex items-center gap-1.5 mt-1"><MapPin size={11} /> {s.location}</div>}
                      {s.details && <div className="text-xs text-slate-500 flex gap-1.5 mt-1"><FileText size={11} className="flex-shrink-0 mt-0.5" /> <span className="whitespace-pre-wrap">{s.details}</span></div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="text-center mt-8 text-[11px] text-slate-400">Sadece görüntüleme yetkisi · Ajans CRM</div>
      </div>
    </div>
  );
}
